import { axiosSetting } from '@/utils/axiosSetting'
import { computed, inject, provide, reactive, toRefs } from 'vue'
import dayjs from 'dayjs'
import { groupBy, isEmpty, orderBy } from 'lodash-es'
import { LoadArchivesProps } from '@/types/Event'
import { getEventDetailJson } from '@/repositories/wpJsonRepository'

const axios = axiosSetting('cms', 'block')
const axiosApp = axiosSetting('app', 'block')

export const createEvent = () => {
  const state = reactive<any>({
    archives: [],
    events: [],
    event: {},
    eventHospitals: {}, // 病院リストと検索用のエリア、病院種別、キーワードのデータが含まれる,
    reservedEvents: [],
    isNoEntry: false,
    loading: false,
    totalArchivePages: 0,
    allArchiveCategories: [],
    allArchiveFilterTypes: [],
    allArchiveTopics: []
  })

  const createTags = (event: any) => {
    const tags = []
    const benefits = event.acf?.event_benefits || event.benefits || []
    const seminars = event.acf?.event_seminar || event.seminars || []
    if (benefits.length) tags.push('イベント特典あり')
    if (seminars.length) tags.push('セミナーあり')
    return tags
  }
  const filterCarrerCategory = (categories: { name: string }[]) => {
    return (
      categories.find((cat) => cat.name === 'キャリアガイダンス') ??
      categories[0]
    )
  }
  const eventMapper = (event: any) => ({
    id: event.id,
    link: `/events/detail/${event.id}`,
    title: event.title.rendered,
    thumbnail: event.thumbnail.url,
    type: event.acf.event_type.label,
    cast_type: event.acf.event_cast_type?.length
      ? event.acf.event_cast_type
      : [],
    term_start: event.acf.event_term.start,
    term_end: event.acf.event_term.end,
    reception: event.acf.event_reception,
    remarks: event.acf.event_remarks,
    benefits: event.acf.event_benefits?.length ? event.acf.event_benefits : [],
    seminars: event.acf.event_seminar?.length ? event.acf.event_seminar : [],
    hospitals: event.acf.event_entry_hospitals?.length
      ? event.acf.event_entry_hospitals
      : [],
    tags: createTags(event),
    archiveTopics: event.event_tag.map((e: any) => e) || [],
    archiveTags: event.event_tag.map((e: any) => e.name) || [],
    category: filterCarrerCategory(event.event_category),
    archiveDescription: event.acf.archive_description,
    isArchived:
      event.acf.archive_flag &&
      dayjs().isAfter(dayjs(event.acf.event_term.end).endOf('day')),
    entry_link: event.acf.event_entry_link,
    entry_start:
      event.acf.event_entry && event.acf.event_entry.start
        ? event.acf.event_entry.start
        : '',
    entry_end:
      event.acf.event_entry && event.acf.event_entry.end
        ? event.acf.event_entry.end
        : '',
    place_name:
      event.acf.event_place && event.acf.event_place.name
        ? event.acf.event_place.name
        : '',
    place_zip:
      event.acf.event_place && event.acf.event_place.zip
        ? event.acf.event_place.zip
        : '',
    place_prefecture:
      event.acf.event_place && event.acf.event_place.prefecture
        ? event.acf.event_place.prefecture
        : '',
    place_city:
      event.acf.event_place && event.acf.event_place.city
        ? event.acf.event_place.city
        : '',
    place_town:
      event.acf.event_place && event.acf.event_place.town
        ? event.acf.event_place.town
        : '',
    place_mapUrl:
      event.acf.event_place && event.acf.event_place.map_url
        ? event.acf.event_place.map_url
        : '',
    place_precaution:
      event.acf.event_place && event.acf.event_place.precaution
        ? event.acf.event_place.precaution
        : '',
    programs_old_layout_flag: event.acf.event_programs_old_layout_flag,
    programs_type: event.acf.event_programs_type,
    programs: event.acf.event_programs?.length ? event.acf.event_programs : [],
    checkin_id: event.acf.event_checkin_id || '',
    filter_type: event.acf.event_filter_type?.length
      ? event.acf.event_filter_type
      : [],
    event_checkin_confirm: event.acf.event_checkin_confirm || false
  })
  const getEvents = (per_page = 100) => {
    axios.get(`event?per_page=${per_page}`).then(({ data }) => {
      state.events = data
        .map((event: any) => eventMapper(event))
        .sort((a: any, b: any) => a.term_start - b.term_end)
      console.log('event_original', data)
      console.log('event_parse', state.events)
    })
  }

  const loadEventsFromHistoryIds = async (
    per_page = 100,
    ids: number[] = []
  ) => {
    const params: {
      per_page: number
      ids?: number[]
    } = {
      per_page: per_page
    }
    if (ids.length > 0) {
      params.ids = ids
    } else {
      state.reservedEvents = []
      return
    }

    state.loading = true

    const { data } = await axios.get('event', { params: params })
    // laravelから取得したイベント履歴の順で、wpから取得したイベント情報を並び替え
    const filteredEventsByHisotry: unknown[] = []
    ids.forEach((historyId) => {
      const isIncludedId = data.find((event: any) => event.id === historyId)
      if (isIncludedId) {
        filteredEventsByHisotry.push(isIncludedId)
      }
    })

    if (filteredEventsByHisotry) {
      state.reservedEvents = filteredEventsByHisotry.map((event: any) =>
        eventMapper(event)
      )
    } else {
      state.reservedEvents = []
    }
    state.loading = false
  }

  const loadArchives = async ({
    per_page = 100,
    page = 1,
    category,
    tag,
    type
  }: LoadArchivesProps) => {
    state.loading = true
    const { data, headers } = await axios.get(`event`, {
      params: {
        archive_flag: 1,
        per_page,
        page,
        category,
        tag,
        type
      }
    })
    state.archives = orderBy(
      data.map((event: any) => eventMapper(event)),
      ['term_start'],
      ['desc']
    )
    state.loading = false
    state.totalArchivePages = Number(headers['x-wp-totalpages'])
    console.log('event_original', data)
    console.log('event_parse', state.archives)
  }

  const eventsByMonth = computed(() => {
    const date: any = groupBy(state.events, (event: any) =>
      dayjs(event.term_start).format('YYYYMM')
    )
    return isEmpty(date) ? { [dayjs().format('YYYYMM')]: [] } : date
  })

  const getEvent = (id: number) => {
    // eslint-disable-next-line
    return new Promise(async (resolve) => {
      if (isNaN(id)) return resolve('')
      if (state.event.id === id) return resolve('')

      const event = state.events.find((event: any) => event.id === id)
      if (event) {
        state.event = event
        console.log('event', event)
        return resolve('')
      }
      state.event = {}

      const { data } = await getEventDetailJson(id)
      console.log('event', data)
      state.event = eventMapper(data)
      console.log('event_fixed', state.event)
      return resolve('')
    })
  }

  const loadEventHospitals = async (): Promise<void> => {
    const hospitalIds: string[] = []
    state.event.programs.forEach((programs: any) => {
      programs.item.forEach((item: any) => {
        if (
          item.hospital_id !== '' &&
          !hospitalIds.includes(item.hospital_id)
        ) {
          hospitalIds.push(item.hospital_id)
        }
      })
    })

    if (hospitalIds.length === 0) {
      state.isNoEntry = true
      return
    }
    const { data } = await axiosApp.post('/api/v1/web_seminar', {
      hospital_id: hospitalIds
    })
    console.log('webSeminar', data)
    state.eventHospitals = data
  }

  const recentEvents = computed(() => {
    return state.events
      .slice(0, 4)
      .filter((event: any) => event.id !== state.event.id)
      .slice(0, 3)
  })

  const createCategories = (events: any[]) => {
    const categories = events
      .map((e: any) => ({
        label: e.category?.name,
        value: e.category?.slug,
        count: 1
      }))
      .filter((category: any) => category.value)
      .reduce((result: any[], category: any) => {
        const dupCategory = result.find((d: any) => d.label === category.label)
        if (dupCategory) dupCategory.count++
        else result.push(category)
        return result
      }, [])
    categories.sort((a: any, b: any) => {
      if (a.value === 'meet_hospital') return -1
      if (b.value === 'meet_hospital') return 1
      return b.count - a.count
    })
    return [
      { label: '目的から探す', value: undefined },
      { label: '全て表示', value: null },
      ...categories
    ]
  }

  const createFilterType = (events: any[]) => {
    const filterTypes = events
      .filter((e: any) => e.filter_type.length)
      .flatMap((e: any) =>
        e.filter_type.map((type: any) => ({
          label: type.label,
          value: type.value,
          count: 1
        }))
      )
      .reduce((result: any[], filterType: any) => {
        const dupFilterType = result.find(
          (d: any) => d.label === filterType.label
        )
        if (dupFilterType) {
          dupFilterType.count++
          return result
        }
        return [...result, filterType]
      }, [])
      .sort((a: any, b: any) => {
        return b.count - a.count
      })
    return [
      { label: '対象から探す', value: undefined },
      { label: '全て表示', value: null },
      ...filterTypes
    ]
  }

  const createTopics = (events: any[]) => {
    let duplicatedTopics: any[] = []
    const allTopics: any[] = []
    events.forEach((archive: any) => {
      if (archive.archiveTopics.length) {
        duplicatedTopics = [...duplicatedTopics, ...archive.archiveTopics]
      }
    })
    duplicatedTopics.forEach((topic) => {
      if (allTopics.some((allTopic) => allTopic.name === topic.name)) {
        return
      }
      allTopics.push(topic)
    })
    return allTopics
  }

  const loadAllArchiveCategorys = async () => {
    const { data } = await axios.get(`event`, {
      params: {
        archive_flag: 1,
        per_page: 100
      }
    })
    const allArchives = orderBy(
      data.map((event: any) => eventMapper(event)),
      ['term_start'],
      ['desc']
    )
    state.allArchiveCategories = createCategories(allArchives)
    state.allArchiveFilterTypes = createFilterType(allArchives)
    state.allArchiveTopics = createTopics(allArchives)
  }

  return {
    createTags,
    getEvent,
    loadEventHospitals,
    getEvents,
    loadArchives,
    loadAllArchiveCategorys,
    loadEventsFromHistoryIds,
    eventsByMonth,
    recentEvents,
    createCategories,
    createFilterType,
    ...toRefs(state)
  }
}

export const key = Symbol()
export const provideEvent = () => provide(key, createEvent())
export const useEvent = (): any => inject(key)
